import { useQuery as useApolloQuery } from '@apollo/client';
import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { RibbonInput } from '@risksmart-app/trpc/src/routers/frontend/my-items.router';
import type {
  MyRibbonChangeRequestsResponseRow,
  MyRibbonItemsResponse,
  MyRibbonRisksResponseRow,
} from '@risksmart-app/trpc/src/types';
import {
  GetMyItemsDashboardDocument,
  type GetMyItemsDashboardQuery,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { useGetQueryVariables } from '../../../pages/dashboards/my-items/hooks/useGetQueryVariables';
import { useMyItemsFilters } from '../../../pages/dashboards/useDashboardStore';

// Shape Ribbon.tsx consumes. Both query branches delegate their full error
// objects to the shared query notification boundary below.
export interface UseGetMyItemsDashboardResult {
  data: GetMyItemsDashboardQuery | undefined;
  loading: boolean;
}

// The legacy Apollo query variables (built by useGetQueryVariables) — passed in
// by the caller so the flag-off branch keeps the existing where-clause builder.
// Typed against the builder's own return type so the *_Bool_Exp shapes (which
// the generated Exact<{...}> variables type doesn't capture) flow through.
export type MyItemsDashboardGraphqlVariables = ReturnType<
  typeof useGetQueryVariables
>;

const mapOwnerGroups = (
  groups: MyRibbonRisksResponseRow['ownerGroups']
): GetMyItemsDashboardQuery['risk'][number]['ownerGroups'] =>
  groups.map((g) => ({
    UserGroupId: g.UserGroupId,
    group: g.group
      ? { users: g.group.users.map((u) => ({ UserId: u.UserId })) }
      : null,
  }));

const mapContributorGroups = (
  groups: MyRibbonRisksResponseRow['contributorGroups']
): GetMyItemsDashboardQuery['risk'][number]['contributorGroups'] =>
  groups.map((g) => ({
    UserGroupId: g.UserGroupId,
    group: g.group
      ? { users: g.group.users.map((u) => ({ UserId: u.UserId })) }
      : null,
  }));

// The drizzle `parent.ObjectType` is the wider domain `ParentType` union; the
// generated `Parent_Type_Enum` is its value subset (the persisted node types).
// Every value the column can hold is a member of `Parent_Type_Enum`, so we
// resolve it by value lookup rather than a cast. The fallback can't be reached
// for a real CR parent node, but keeps the function total — `ObjectType` only
// feeds `getOwners`'s string comparisons downstream, so the fallback is inert.
const parentTypeEnumValues = Object.values(Parent_Type_Enum);

const toParentTypeEnum = (objectType: string): Parent_Type_Enum =>
  parentTypeEnumValues.find((v) => v === objectType) ?? Parent_Type_Enum.Risk;

const mapChangeRequest = (
  cr: MyRibbonChangeRequestsResponseRow
): GetMyItemsDashboardQuery['change_request'][number] => ({
  ChangeRequestStatus: cr.ChangeRequestStatus,
  CreatedAtTimestamp: cr.CreatedAtTimestamp,
  Id: cr.Id,
  Workflow: cr.Workflow,
  RequestedChanges: cr.RequestedChanges,
  responses: cr.responses.map((r) => ({
    Approved: r.Approved,
    approver: {
      OwnerApprover: r.approver.OwnerApprover,
      level: r.approver.level
        ? {
            Id: r.approver.level.Id,
            ApprovalRuleType: r.approver.level.ApprovalRuleType,
          }
        : null,
      group: r.approver.group
        ? { users: r.approver.group.users.map((u) => ({ UserId: u.UserId })) }
        : null,
      user: r.approver.user ? { Id: r.approver.user.Id } : null,
    },
  })),
  parent: cr.parent
    ? {
        Id: cr.parent.Id,
        SequentialId: cr.parent.SequentialId,
        ObjectType: toParentTypeEnum(cr.parent.ObjectType),
        risk: cr.parent.risk
          ? { Id: cr.parent.risk.Id, Title: cr.parent.risk.Title }
          : null,
        documentFile: cr.parent.documentFile
          ? {
              Version: cr.parent.documentFile.Version,
              parent: cr.parent.documentFile.parent
                ? {
                    Id: cr.parent.documentFile.parent.Id,
                    Title: cr.parent.documentFile.parent.Title,
                  }
                : null,
            }
          : null,
        action: cr.parent.action
          ? { Id: cr.parent.action.Id, Title: cr.parent.action.Title }
          : null,
        issue_assessment: cr.parent.issue_assessment
          ? {
              parent: cr.parent.issue_assessment.parent
                ? {
                    Id: cr.parent.issue_assessment.parent.Id,
                    Title: cr.parent.issue_assessment.parent.Title,
                  }
                : null,
            }
          : null,
        acceptance: cr.parent.acceptance
          ? { Id: cr.parent.acceptance.Id, Title: cr.parent.acceptance.Title }
          : null,
        control: cr.parent.control
          ? { Id: cr.parent.control.Id, Title: cr.parent.control.Title }
          : null,
        issue: cr.parent.issue
          ? { Id: cr.parent.issue.Id, Title: cr.parent.issue.Title }
          : null,
      }
    : null,
  currentUserOwnerList: cr.currentUserOwnerList.map((o) => ({
    UserId: o.UserId,
  })),
});

/**
 * Composes the aggregate tRPC ribbon response into the legacy
 * `GetMyItemsDashboardQuery` shape so the downstream ribbon hooks
 * (useFlattenRibbonData / useRibbonNavigation / useGetMyItemsFilteringTokens)
 * keep working unchanged. Field-by-field, no casts — `__typename` is omitted
 * (optional in the generated type) and nullables are coalesced explicitly.
 */
export const mapTrpcRibbonDataToGraphQL = (
  data: MyRibbonItemsResponse
): GetMyItemsDashboardQuery => ({
  change_request: data.changeRequests.map(mapChangeRequest),
  action: data.actions.map((d) => ({
    Id: d.Id,
    // Legacy `action.Status` is non-null `Action_Status_Enum`; the ribbon read
    // selects the same NOT NULL column.
    Status: d.Status,
    // `DateDue` is non-null in the legacy contract; the ribbon read selects the
    // same NOT NULL column.
    DateDue: d.DateDue,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  risk: data.risks.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  indicator: data.indicators.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  document: data.documents.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  assessment: data.assessments.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  assessment_activity: data.assessmentActivities.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
  })),
  control: data.controls.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  issue: data.issues.map((d) => ({
    Id: d.Id,
    assessment: d.assessment ? { Status: d.assessment.Status } : null,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  attestation_record: data.attestationRecords.map((d) => ({ Id: d.Id })),
  obligation: data.obligations.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
});

/**
 * Builds the shared `RibbonInput` from the SAME dashboard store state the legacy
 * `getQueryVariables` reads: the 8 ownership flags pass through unmodified and
 * `entityIds` is only attached when non-empty. Ownership is always scoped to the
 * logged-in user server-side.
 */
const useRibbonInput = (): RibbonInput => {
  const myItemsFilters = useMyItemsFilters();
  const { entityIds } = useEntityFilter();

  return useMemo(() => {
    return {
      owner: myItemsFilters.owner,
      contributor: myItemsFilters.contributor,
      groupOwner: myItemsFilters.groupOwner,
      groupContributor: myItemsFilters.groupContributor,
      inheritedOwner: myItemsFilters.inheritedOwner,
      inheritedContributor: myItemsFilters.inheritedContributor,
      inheritedGroupOwner: myItemsFilters.inheritedGroupOwner,
      inheritedGroupContributor: myItemsFilters.inheritedGroupContributor,
      // entityIds only narrows risk/control/issue/action/indicator; omit when
      // empty so the backend treats it as "no entity filter".
      ...(entityIds.length > 0 ? { entityIds } : {}),
    };
  }, [myItemsFilters, entityIds]);
};

/**
 * RSP-6214 — flag-gated my-items ribbon read. With the `trpc` flag ON it runs
 * one aggregate `frontend.myItems.ribbonItems` query and composes the response
 * into the legacy `GetMyItemsDashboardQuery` shape; with the flag OFF it runs
 * the legacy `GetMyItemsDashboardDocument` Apollo mega-query (default). Only
 * one branch's query executes — the other is gated via
 * `enabled` / `skip` so the vitest console.warn-is-fatal setup never trips on a
 * stray dual fetch.
 *
 * The caller passes the legacy Apollo `variables` (built by useGetQueryVariables)
 * for the flag-off branch; the tRPC input is derived internally from the SAME
 * dashboard store state.
 */
export const useGetMyItemsDashboard = (
  graphqlVariables: MyItemsDashboardGraphqlVariables
): UseGetMyItemsDashboardResult => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const ribbonInput = useRibbonInput();

  const ribbonItems = useQuery({
    ...trpc.frontend.myItems.ribbonItems.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });

  const trpcLoading = trpcEnabled && ribbonItems.isLoading;
  const trpcError = trpcEnabled ? ribbonItems.error : undefined;

  useErrorNotification(trpcError, trpcEnabled);

  const trpcData = useMemo((): GetMyItemsDashboardQuery | undefined => {
    if (!trpcEnabled || ribbonItems.data === undefined) {
      return undefined;
    }

    return mapTrpcRibbonDataToGraphQL(ribbonItems.data);
  }, [trpcEnabled, ribbonItems.data]);

  // --- Apollo branch: legacy mega-query, skipped when the flag is on ---
  const {
    data: graphqlData,
    loading: graphqlLoading,
    error: graphqlError,
  } = useApolloQuery(GetMyItemsDashboardDocument, {
    variables: graphqlVariables,
    fetchPolicy: 'no-cache',
    skip: trpcEnabled,
  });

  useErrorNotification(graphqlError, !trpcEnabled);

  if (trpcEnabled) {
    return { data: trpcData, loading: trpcLoading };
  }

  return { data: graphqlData, loading: graphqlLoading };
};
